import { cloneDeep, get, isEqual } from 'lodash';
import { AnyAction } from 'redux';

export const RESET_STATE = '@@reset/RESET_STATE';

export const createResetAction = (paths: string[]) => ({
  type: RESET_STATE,
  payload: paths,
});

/**
 * Sets a value at a nested path while creating new object references along the way
 * to ensure immutability
 */
const immutableSet = (obj: any, path: string, value: any): any => {
  const pathArray = path.split('.');
  const key = pathArray[0];

  // Base case: we're at the final key
  if (pathArray.length === 1) {
    return { ...obj, [key]: value };
  }

  // Create a new object with the updated nested path
  const remainingPath = pathArray.slice(1).join('.');
  return {
    ...obj,
    [key]: immutableSet(obj[key] || {}, remainingPath, value),
  };
};

export const createReducerWithReset = <T extends object>(
  initialState: T,
  reducer: (state: T, action: AnyAction) => T,
) => {
  return (state: T = initialState, action: AnyAction): T => {
    if (action.type === RESET_STATE) {
      const paths: string[] = action.payload;
      let hasChanges = false;
      let newState = state;

      paths.forEach((path) => {
        const currentValue = get(state, path);
        const initialValue = get(initialState, path);

        if (!isEqual(currentValue, initialValue)) {
          // Only create a new state reference if we haven't done so yet
          if (!hasChanges) {
            newState = { ...state };
            hasChanges = true;
          }

          // Update the state immutably
          newState = immutableSet(newState, path, cloneDeep(initialValue));
        }
      });

      return hasChanges ? newState : state;
    }
    return reducer(state, action);
  };
};
